/**
 * Quality-Adaptive Reactive Failover (Patent Sub-system K)
 * =========================================================
 * Most routers fail over to a cheaper model and ship the request unchanged.
 * We rewrite the system prompt to inject compensating instructions derived
 * from a stored quality-differential profile.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  Shield, RefreshCw, AlertTriangle, Sparkles, ArrowRight,
  Loader2, FlaskConical, Activity, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PROMPT = `You are a senior code reviewer for a Python codebase. Review the diff and call out:
1. Correctness bugs
2. Security issues (injection, auth, secrets)
3. Performance issues (N+1, hot loops, missing caching)
4. Style issues (only if egregious)

Be concise. One line per issue. No editorial.`;

const QAFailover = () => {
  const [profiles, setProfiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [primary, setPrimary] = useState('claude-sonnet-latest');
  const [fallback, setFallback] = useState('claude-haiku-latest');
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_PROMPT);
  const [failureReason, setFailureReason] = useState('529 Overloaded');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/qa-failover/profiles`)
      .then((r) => setProfiles(r.data.profiles || []))
      .catch(() => {});
    axios.get(`${API_URL}/qa-failover/events-public`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => {});
  }, []);

  const simulate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/qa-failover/simulate`, {
        system_prompt: systemPrompt,
        primary_model: primary,
        fallback_model: fallback,
        failure_reason: failureReason,
      });
      setResult(r.data);
      toast.success(`Compensation applied · score ${(r.data.quality_compensation_score * 100).toFixed(0)}%`);
    } catch (e) {
      toast.error('Simulate failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="qa-failover-page">
      <Helmet>
        <title>Quality-Adaptive Reactive Failover | CARTIEAI</title>
        <meta name="description" content="When upstream LLM degrades, we don't just fail over — we rewrite the system prompt to compensate for the quality gap. Patent-pending sub-system K." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-rose-300 border border-rose-500/30 rounded-md px-2 py-1 mb-5" data-testid="qa-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>QA Failover</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            When upstream degrades, the system gets <span className="text-white font-semibold">smarter</span>, not dumber.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            LiteLLM, Portkey, OpenRouter all fail over without touching the prompt — your customers feel the quality drop. We rewrite the system prompt to inject compensating instructions derived from a stored quality-differential profile (Sonnet→Haiku, 4o→4o-mini, cross-provider). Smoother degradation, fewer support tickets.
          </p>
        </div>

        {/* SIMULATOR */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-rose-300" /> Failover inputs
              </CardTitle>
              <CardDescription>Choose primary + fallback model + failure reason.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-400">Primary model</Label>
                <Select value={primary} onValueChange={setPrimary}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white mt-1" data-testid="qa-primary-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="claude-sonnet-latest">claude-sonnet-latest</SelectItem>
                    <SelectItem value="gpt-4o">gpt-4o</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Fallback model</Label>
                <Select value={fallback} onValueChange={setFallback}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white mt-1" data-testid="qa-fallback-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="claude-haiku-latest">claude-haiku-latest</SelectItem>
                    <SelectItem value="gpt-4o-mini">gpt-4o-mini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Failure reason (optional)</Label>
                <Select value={failureReason} onValueChange={setFailureReason}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white mt-1" data-testid="qa-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="529 Overloaded">529 Overloaded</SelectItem>
                    <SelectItem value="rate_limit_exceeded">429 Rate Limit</SelectItem>
                    <SelectItem value="503 Service Unavailable">503 Service Unavailable</SelectItem>
                    <SelectItem value="timeout">Timeout</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Original system prompt</Label>
                <Textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  className="font-mono text-xs bg-slate-950 border-white/10 text-white min-h-[160px] mt-1"
                  data-testid="qa-prompt-input"
                />
              </div>
              <Button onClick={simulate} disabled={loading} className="w-full bg-rose-500 hover:bg-rose-400 text-slate-950 font-semibold" data-testid="qa-simulate-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Shield className="w-4 h-4 mr-1.5" />}
                Simulate failover
              </Button>
            </CardContent>
          </Card>

          <Card className="lg:col-span-3 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-rose-300" /> Transformed prompt
              </CardTitle>
              <CardDescription>What the fallback model will actually receive.</CardDescription>
            </CardHeader>
            <CardContent>
              {!result && (
                <div className="text-center py-14 text-slate-500 text-sm" data-testid="qa-result-empty">
                  <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  Click Simulate to preview the failover transformation.
                </div>
              )}
              {result && (
                <div data-testid="qa-result">
                  <div className="flex items-center gap-2 mb-3 text-xs">
                    <Badge variant="outline" className="border-white/10 text-slate-300 font-mono">{result.primary_model}</Badge>
                    <ArrowRight className="w-3 h-3 text-slate-300" />
                    <Badge variant="outline" className="border-rose-400/40 text-rose-200 font-mono">{result.fallback_model}</Badge>
                    <Badge className="ml-auto bg-emerald-500/20 text-emerald-300 border-emerald-400/30">
                      compensation score · {(result.quality_compensation_score * 100).toFixed(0)}%
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-400 italic mb-3 leading-relaxed">{result.rationale}</div>
                  <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold mb-1.5">Injected compensation</div>
                    <pre className="text-[11px] text-emerald-100 whitespace-pre-wrap font-mono">{result.injection_used}</pre>
                  </div>
                  <div className="rounded-md border border-white/10 bg-slate-950 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Final system prompt sent to fallback</div>
                    <pre className="text-[11px] text-slate-300 whitespace-pre-wrap font-mono max-h-[400px] overflow-auto">{result.transformed_system_prompt}</pre>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* DIFFERENTIAL PROFILES CATALOG */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-rose-300 uppercase mb-2">Quality-differential profiles</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">{profiles.length} profiles shipped today. Each one captures the known capability gap between a stronger primary and a cheaper fallback — and the exact compensation that closes it.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="qa-profile-catalog">
            {profiles.map((p) => (
              <div key={`${p.primary_model}__${p.fallback_model}`} className="bg-slate-900/60 border border-white/10 rounded-md p-4">
                <div className="flex items-center gap-2 mb-2 text-[11px]">
                  <Badge variant="outline" className="border-white/10 text-slate-300 font-mono">{p.primary_model}</Badge>
                  <ArrowRight className="w-3 h-3 text-slate-300" />
                  <Badge variant="outline" className="border-rose-400/40 text-rose-200 font-mono">{p.fallback_model}</Badge>
                </div>
                <div className="text-xs text-slate-400 leading-relaxed">{p.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RECENT EVENTS */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-rose-300 uppercase mb-2">Recent failover events</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">Demo-seed events shown for empty tenants. Real events accumulate as the proxy executes failovers in production.</p>
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="qa-events-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold p-3">When</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold p-3">Primary → Fallback</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold p-3">Reason</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold p-3">Feature</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold p-3">Compensation</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-white/5">
                      <td className="p-3 text-xs text-slate-400 font-mono">{new Date(e.ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="p-3 text-xs text-slate-300 font-mono">{e.primary_model} → {e.fallback_model}</td>
                      <td className="p-3 text-xs text-amber-300 font-mono">{e.failure_reason}</td>
                      <td className="p-3 text-xs text-slate-300">{e.feature_slug || '—'}</td>
                      <td className="p-3 text-xs">
                        {e.compensation_applied ? (
                          <span className="text-emerald-300 font-medium">✓ applied</span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-xs">No events yet.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QAFailover;
